import { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import DashboardLayout from "@/components/layouts/DashboardLayout";
import { PaymentHistoryTable } from "@/components/payments/PaymentHistoryTable";
import { PaymentHeader } from "@/components/payments/PaymentHeader";
import { useToast } from "@/components/ui/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ArrowUpRight, CreditCard, DollarSign, TrendingUp } from "lucide-react";

export default function Payments() {
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({
    from: undefined,
    to: undefined,
  });
  const { toast } = useToast();

  const {
    data: payments,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["payments"],
    queryFn: async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error("No authenticated session");

      let query = supabase
        .from("payment_transactions")
        .select(
          `
          *,
          tax_return_requests (
            financial_year,
            service_type
          ),
          profiles (
            email,
            full_name
          )
        `
        )
        .eq("user_id", session.user.id)
        .order("created_at", { ascending: false });

      // Apply date range filter if set
      if (dateRange.from) {
        query = query.gte("created_at", dateRange.from.toISOString());
      }
      if (dateRange.to) {
        query = query.lte("created_at", dateRange.to.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
  });

  useEffect(() => {
    const channel = supabase
      .channel("payment-updates")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "payment_transactions",
        },
        () => {
          refetch();
          toast({
            title: "Payment Updated",
            description: "Your payment information has been updated.",
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetch, toast]);

  // Filter payments in memory instead of using database LIKE operations
  const filteredPayments = payments?.filter((payment) => {
    if (!searchQuery) return true;

    const searchLower = searchQuery.toLowerCase();
    return (
      payment.id.toLowerCase().includes(searchLower) ||
      (payment.tax_return_requests?.service_type || "")
        .toLowerCase()
        .includes(searchLower) ||
      (payment.tax_return_requests?.financial_year || "")
        .toLowerCase()
        .includes(searchLower) ||
      payment.status.toLowerCase().includes(searchLower)
    );
  });

  // Calculate payment statistics
  const paymentStats = useMemo(() => {
    if (!payments) return { total: 0, completed: 0, pending: 0, recent: 0 };

    const total = payments.reduce((sum, payment) => sum + payment.amount, 0);
    const completed = payments
      .filter((p) => p.status.toLowerCase() === "completed")
      .reduce((sum, payment) => sum + payment.amount, 0);

    const pending = payments
      .filter((p) => ["pending", "processing"].includes(p.status.toLowerCase()))
      .reduce((sum, payment) => sum + payment.amount, 0);

    // Payments in the last 30 days
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const recent = payments
      .filter((p) => new Date(p.created_at) >= thirtyDaysAgo)
      .reduce((sum, payment) => sum + payment.amount, 0);

    return { total, completed, pending, recent };
  }, [payments]);

  if (error) {
    console.error("Error fetching payments:", error);
    toast({
      title: "Error",
      description: "Failed to fetch payment history. Please try again.",
      variant: "destructive",
    });
  }

  return (
    <DashboardLayout>
      <Helmet>
        <title>Payment History - NumberzInsight</title>
        <meta
          name="description"
          content="View your payment history and invoices"
        />
      </Helmet>

      <div className="space-y-6">
        <PaymentHeader
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
        />

        {/* Payment Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Payments
              </CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${paymentStats.total.toFixed(2)}
              </div>
              <p className="text-xs text-muted-foreground">
                Lifetime payment total
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Completed</CardTitle>
              <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${paymentStats.completed.toFixed(2)}
              </div>
              <p className="text-xs text-muted-foreground">
                Successfully processed payments
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${paymentStats.pending.toFixed(2)}
              </div>
              <p className="text-xs text-muted-foreground">
                Payments awaiting processing
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Last 30 Days
              </CardTitle>
              <CreditCard className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${paymentStats.recent.toFixed(2)}
              </div>
              <p className="text-xs text-muted-foreground">
                Recent payment activity
              </p>
            </CardContent>
          </Card>
        </div>

        <PaymentHistoryTable
          payments={filteredPayments || []}
          isLoading={isLoading}
        />
      </div>
    </DashboardLayout>
  );
}
